export class ResponseError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response: Response) {
  if (
    response.status === 204 ||
    response.status === 205 ||
    response.status === 404
  ) {
    return null;
  }
  return response.json();
}

export interface Res<T> {
  data: T | null;
  status: number;
  err?: ResponseError;
  error?: ResponseError;
  message?: string;
}

export type ResponseType = "json" | "text" | "blob";

export type RequestOptions = RequestInit & {
  responseType?: ResponseType;
};

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
async function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return { data: response, status: response.status };
  }
  const error = new ResponseError(response);
  const res = await response.text();
  error.message = res;
  error.response = response;
  return { err: error, status: response.status, data: null };
}

const defaultOptions = () => {
  const sessionToken = localStorage.getItem("accessToken");
  const language = localStorage.getItem("i18nextLng");
  return {
    headers: {
      "Content-Type": "application/json",
      "X-Custom-Lang": language || "en",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    },
  };
};

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export async function request(
  url: string,
  options?: RequestOptions,
): Promise<Res<{}>> {
  const { responseType = "json", ...fetchOptions } = options || {};
  const payload: RequestInit = {
    ...defaultOptions(),
    ...fetchOptions,
  };

  try {
    let res = await fetch(url, payload);
    let response = await checkStatus(res);

    if (response.status === 401) {
      localStorage.clear();
      window.location.reload();

      // just remove this concept due to we don't have followed the  refresh token concepts
      // Attempt to refresh the token
      // await refreshToken();
      // Retry the original request with the same payload
      // res = await fetch(url, payload);
      // response = await checkStatus(res);
    }

    // if (response.status === 5000) {
    //   localStorage.clear();
    //   window.location.reload()
    // }
    if (response.err) {
      // return response;
      let errBody: any = null;
      try {
        // It's already a string on the error object — parse it directly
        const errString = response.err?.message || JSON.stringify(response.err);
        errBody =
          typeof errString === "string" ? JSON.parse(errString) : errString;
      } catch (_) {}

      return {
        data: null,
        err: {
          ...response.err,
          message:
            errBody?.message || response.err?.message || "Request failed",
          statusCode: errBody?.statusCode,
        } as ResponseError,
        error: {
          ...response.err,
          message:
            errBody?.message || response.err?.message || "Request failed",
          statusCode: errBody?.statusCode,
        } as ResponseError,
        status: response.status,
      };
    }

    let data: unknown = null;

    if (responseType === "text") {
      data = await response.data.text();
    } else if (responseType === "blob") {
      data = await response.data.blob();
    } else {
      data = await parseJSON(response.data);
    }

    return { data: data as {}, status: response.status };
  } catch (error) {
    return {
      data: null,
      err: error as ResponseError,
      error: error as ResponseError,
      status: 500,
    }; // Handle unexpected errors
  }
}
